import {
  Bell,
  Check,
  MessageSquare,
  CircleCheck,
  Calendar,
  FileText,
  Sparkles,
  Trash2,
  X,
  Loader2,
  AlertCircle,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState } from "react";
import {
  useNotifications,
  useMarkAllRead,
  useDismissNotification,
} from "@/hooks/useNotifications";
import type { Notification } from "@/services/notification.service";

/** Map notification type to icon */
const iconMap: Record<string, typeof Bell> = {
  message: MessageSquare,
  task: CircleCheck,
  milestone: Calendar,
  document: FileText,
  system: Sparkles,
};

/** Format relative time from ISO string */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `about ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export const NotificationsPopover = () => {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useNotifications();
  const markAllRead = useMarkAllRead();
  const dismiss = useDismissNotification();

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="header-notifications-button">
          <Bell className="size-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-[18px] min-w-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold grid place-items-center shadow-sm ring-2 ring-card"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className="w-[calc(100vw-24px)] sm:w-[420px] p-0 overflow-hidden border-border/60 shadow-lg"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-border/60">
          <div className="flex items-start gap-3">
            <Bell className="size-5 mt-0.5 text-muted-foreground" />
            <div>
              <div className="text-[15px] font-semibold leading-tight">
                Notifications
              </div>
              {unreadCount > 0 && (
                <div className="text-[12px] text-muted-foreground mt-0.5">
                  {unreadCount} unread
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-foreground bg-muted hover:bg-muted/70 border border-border/60 transition-colors px-2.5 py-1.5 rounded-md disabled:opacity-50"
                data-testid="notifications-mark-all-read-button"
              >
                {markAllRead.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Check className="size-3.5" />
                )}{" "}
                Mark all read
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="ml-1 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
              data-testid="notifications-close-button"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="max-h-[440px] overflow-y-auto">
          {isLoading && (
            <div className="px-5 py-12 text-center">
              <Loader2 className="size-5 animate-spin mx-auto text-muted-foreground" />
            </div>
          )}
          {error && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <AlertCircle className="size-5 text-destructive" />
              Unable to load notifications
            </div>
          )}
          {!isLoading && !error && notifications.length === 0 && (
            <div className="px-5 py-12 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          )}
          {notifications.map((n: Notification) => {
            const Icon = iconMap[n.type] ?? Bell;
            return (
              <div
                key={n.id}
                className={cn(
                  "group relative flex gap-3 px-5 py-3.5 border-b border-border/60 last:border-b-0 hover:bg-muted/40 transition-colors",
                  !n.read && "bg-destructive/[0.04]"
                )}
              >
                <div className="h-8 w-8 shrink-0 rounded-full bg-muted text-muted-foreground grid place-items-center">
                  <Icon className="size-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold leading-snug flex items-center gap-1.5">
                    <span>{n.title}</span>
                    {!n.read && (
                      <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                    )}
                  </div>
                  <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">
                    {n.body}
                  </p>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground">
                      {relativeTime(n.createdAt)}
                    </span>
                    {n.actionLabel && (
                      <button className="text-[12px] font-medium text-primary hover:underline" data-testid={`notification-action-${n.id}`}>
                        {n.actionLabel} →
                      </button>
                    )}
                  </div>
                </div>
                {n.dismissible && (
                  <button
                    onClick={() => dismiss.mutate(n.id)}
                    className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Dismiss"
                    data-testid={`notification-dismiss-${n.id}`}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
